import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader } from "../../../ui/card";
import { Button } from "../../../ui/button";
import { Badge } from "../../../ui/badge";
import { Input } from "../../../ui/input";
import { ScrollArea } from "../../../ui/scroll-area";
import { Switch } from "../../../ui/switch";

import {
  Box,
  Database,
  Server,
  Key,
  Lock,
  Layers,
  FileText,
  HardDrive,
  Network,
  Container,
  Search,
  ChevronRight,
  ArrowLeft,
  ChevronLeft,
  Info,
  X,
  ChevronsUpDown,
  Check,
  FileCode,
  Cpu,
  Briefcase,
  UserCircle,
  Settings,
  ShieldCheck,
  Clock,
  Globe,
  Shield,
  Cloud,
  Gauge,
} from "lucide-react";
import { SiKubernetes } from "react-icons/si";
import { DetailsDialog } from "../DetailsDialog";
import { awsClusters, azureClusters, gcpClusters, getResourceTypes, updateK8ResourcePublish, viewTabDetails } from "./data";
import { myLocalStorage } from "../../../StorageHelper";
import { publishAWSCluster, publishAzureCluster, publishGCPCluster } from "./Api/resourceapi";
import CloudResourceAnalytics from "./CloudResourceAnalytics";
import "../CloudStatus.css";

const ITEMS_PER_PAGE = 12;


// K8s resource types (same as Tenant)
const resourceTypes = [
  { id: "namespace", name: "Namespace", icon: Layers },
  { id: "secret", name: "Secret", icon: Lock },
  { id: "node", name: "Node", icon: Server },
  { id: "serviceaccount", name: "Service Account", icon: Key },
  { id: "deployment", name: "Deployment", icon: Container },
  { id: "pod", name: "Pod", icon: Box },
  { id: "service", name: "Service", icon: Network },
  { id: "configmap", name: "ConfigMap", icon: FileText },
  { id: "persistentvolume", name: "Persistent Volume", icon: HardDrive },
  { id: "storageclass", name: "Storage Class", icon: Database },
];

interface K8sResource {
  id: string;
  name: string;
  type: string;
  cluster: string;
  namespace?: string;
  createdAt: string;
  status: "running" | "pending" | "available";
}

const nameSpaceLevelType = [
  { id: "1", name: "Deployment", type: "DEPLOYMENT", icon: Container },
  { id: "2", name: "Service Account", type: "SERVICE_ACCOUNT", icon: Key },
  { id: "3", name: "Secret", type: "SECRET", icon: Lock },
  { id: "4", name: "Config Map", type: "CONFIG_MAP", icon: FileText },
  { id: "5", name: "Network Policy", type: "NETWORK_POLICY", icon: Shield },
  { id: "6", name: "Job", type: "JOB", icon: FileText },
  { id: "7", name: "Cron Job", type: "CRON_JOB", icon: Clock },
  { id: "8", name: "Replica Set", type: "REPLICA_SET", icon: Layers },
  { id: "9", name: "Stateful Set", type: "STATEFUL_SET", icon: Cloud },
  { id: "10", name: "Daemon Set", type: "DAEMON_SET", icon: Gauge },
  { id: "11", name: "Service", type: "SERVICE", icon: Network },
  { id: "12", name: "Ingress", type: "INGRESS", icon: Globe },
];


export interface AdminKubernetesProps {
  cloudProvider: string;
  onAnalyticsViewChange?: (isOpen: boolean) => void;
}

export default function AdminKubernetes({ cloudProvider, onAnalyticsViewChange }: AdminKubernetesProps) {
  const [selectedCluster, setSelectedCluster] = useState<string | null>(null);
  const [selectedAnalyticsResource, setSelectedAnalyticsResource] = useState<any>(null);
  const [selectedNamespace, setSelectedNamespace] = useState<string | null>(null);
  const [selectedResourceType, setSelectedResourceType] = useState<string | null>(null);

  // Top-level tabs for cluster view
  const [clusterViewTab, setClusterViewTab] = useState<"namespace" | "CUSTOM_RESOURCE_DEFINITION" | "node" | "storage_class" | "persistent_volume">("namespace");
  const [selectedCRD, setSelectedCRD] = useState<any>(null);
  const [selectedNode, setSelectedNode] = useState<any>(null);
  const [selectedStorageClass, setSelectedStorageClass] = useState<any>(null);
  const [selectedPV, setSelectedPV] = useState<any>(null);
  const [clusters, setClusters] = useState<any[]>([]);
  const [crd, setCRD] = useState<any[]>([]);
  const [nodes, setNodes] = useState<any[]>([]);
  const [storages, setStorages] = useState<any[]>([]);
  const [pvt, setPVT] = useState<any[]>([]);
  const [nameSpaces, setNameSpaces] = useState<any[]>([]);


  // Search and pagination states
  const [clusterSearchQuery, setClusterSearchQuery] = useState("");
  const [namespaceSearchQuery, setNamespaceSearchQuery] = useState("");
  const [typeSearchQuery, setTypeSearchQuery] = useState("");
  const [resourceSearchQuery, setResourceSearchQuery] = useState("");
  const [crdSearchQuery, setCrdSearchQuery] = useState("");
  const [nodeSearchQuery, setNodeSearchQuery] = useState("");
  const [storageClassSearchQuery, setStorageClassSearchQuery] = useState("");
  const [pvSearchQuery, setPvSearchQuery] = useState("");
  const [clusterPage, setClusterPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentResources, setCurrentResources] = useState<any[]>([]);

  // Publish states - initialize from namespaces
  const namespaceList = nameSpaces;

  const [clusterPublishStates, setClusterPublishStates] = useState<Record<string, boolean>>({});
  const [namespacePublishStates, setNamespacePublishStates] = useState<Record<string, boolean>>({});
  const [resourcePublishStates, setResourcePublishStates] = useState<Record<string, boolean>>({});

  // Cluster search and pagination
  const filteredClusters = clusters.filter((cluster) =>
    cluster.name?.toLowerCase().includes(clusterSearchQuery.toLowerCase())
  );

  const totalClusterPages = Math.ceil(filteredClusters.length / ITEMS_PER_PAGE);
  const clusterStartIndex = (clusterPage - 1) * ITEMS_PER_PAGE;
  const paginatedClusters = filteredClusters.slice(clusterStartIndex, clusterStartIndex + ITEMS_PER_PAGE);

  const handleClusterSearch = (query: string) => {
    setClusterSearchQuery(query);
    setClusterPage(1);
  };

  // Filter namespaces
  const filteredNamespaces = namespaceList.filter(ns =>
    ns.name.toLowerCase().includes(namespaceSearchQuery.toLowerCase())
  );

  // Get all resources for selected cluster, filter by namespace
  const getAllResourcesForNamespace = () => {
    if (!selectedCluster || !selectedNamespace) return {};

    const clusterData = {};
    const filteredResources: Record<string, K8sResource[]> = {};

    // Filter each resource type by namespace
    Object.entries(clusterData).forEach(([type, resources]) => {
      if (type === 'namespace') return;

      const filtered: any[] = []

      if (filtered.length > 0) {
        filteredResources[type] = filtered;
      }
    });

    return filteredResources;
  };

  const clusterResources = getAllResourcesForNamespace();

  // Get resource types that have resources for selected namespace
  const getResourceTypesForNamespace = () => {
    return resourceTypes.filter(type => {
      const resources = clusterResources[type.id];
      return resources && resources.length > 0;
    });
  };

  const filteredResourceTypes = getResourceTypesForNamespace().filter(type =>
    type.name.toLowerCase().includes(typeSearchQuery.toLowerCase())
  );



  const searchFilteredResources = currentResources?.filter(resource =>
    resource.name.toLowerCase().includes(resourceSearchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(searchFilteredResources?.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedResources = searchFilteredResources?.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleResourceTypeChange = (typeId: string) => {
    setSelectedResourceType(typeId);
    setCurrentPage(1);
    setResourceSearchQuery("");
  };

  const handleResourceSearch = (query: string) => {
    setResourceSearchQuery(query);
    setCurrentPage(1);
  };

  const handleBackToList = () => {
    setSelectedCluster(null);
    setSelectedNamespace(null);
    setSelectedResourceType(null);
    setCurrentPage(1);
    setResourceSearchQuery("");
    setTypeSearchQuery("");
    setNamespaceSearchQuery("");
  };

  const openResourceAnalytics = (resource: any, resourceType: string) => {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName || "";
    const selectedSubList = JSON.parse(myLocalStorage.getItem("latestSubList") || "{}");
    const projectId =
      cloudProvider === "gcp"
        ? myLocalStorage.getItem("gcpSelectedProjectId")
        : cloudProvider === "azure"
          ? selectedSubList?.azureSubscriptionId
          : myLocalStorage.getItem("selectedAwsAccount");

    setSelectedAnalyticsResource({
      resource,
      cloudProvider,
      projectId,
      tenantName: latestTenant,
      resourceName: resource.name,
      resourceType,
    });
    onAnalyticsViewChange?.(true);
  };


  // Filter lists for other tabs
  const filteredCRDs = crd.filter(crd =>
    crd.name.toLowerCase().includes(crdSearchQuery.toLowerCase())
  );

  const filteredNodes = nodes.filter(node =>
    node.name.toLowerCase().includes(nodeSearchQuery.toLowerCase())
  );

  const filteredStorageClasses = storages.filter(sc =>
    sc.name.toLowerCase().includes(storageClassSearchQuery.toLowerCase())
  );

  const filteredPVs = pvt.filter(pv =>
    pv.name.toLowerCase().includes(pvSearchQuery.toLowerCase())
  );

  const toggleResourceCategoryPublish = async (resourceCategory: any, currentCluster: any, type: string) => {

    let payload = {
      resourceId: resourceCategory.uid,
      flag: !resourceCategory.published,
      type: type.toUpperCase(),
      cloudProviderType: cloudProvider.toUpperCase(),
      clusterId: currentCluster.id,
    }
    await updateK8ResourcePublish(payload, cloudProvider);

    if (type.toUpperCase() === "NAMESPACE") {
      setNameSpaces((prev: any[]) =>
        prev.map((item) =>
          item.uid === resourceCategory.uid
            ? { ...item, published: !item.published }
            : item
        )
      );
    } else if (type.toUpperCase() === "CUSTOM_RESOURCE_DEFINITION") {
      setCRD((prev: any[]) =>
        prev.map((item) =>
          item.uid === resourceCategory.uid
            ? { ...item, published: !item.published }
            : item
        )
      );
    } else if (type.toUpperCase() === "NODE") {
      setNodes((prev: any[]) =>
        prev.map((item) =>
          item.uid === resourceCategory.uid
            ? { ...item, published: !item.published }
            : item
        )
      );
    } else if (type.toUpperCase() === "STORAGE_CLASS") {
      setStorages((prev: any[]) =>
        prev.map((item) =>
          item.uid === resourceCategory.uid
            ? { ...item, published: !item.published }
            : item
        )
      );
    } else if (type.toUpperCase() === "PERSISTENT_VOLUME") {
      setPVT((prev: any[]) =>
        prev.map((item) =>
          item.uid === resourceCategory.uid
            ? { ...item, published: !item.published }
            : item
        )
      );
    }



  };

  const toggleResourcePublish = async (resource: any, currentCluster: any, type: string) => {
    let payload = {
      resourceId: resource.uid,
      flag: !resource.published,
      type: type.toUpperCase(),
      cloudProviderType: cloudProvider.toUpperCase(),
      clusterId: currentCluster.id,
    }
    await updateK8ResourcePublish(payload, cloudProvider);

    setCurrentResources((prev: any[]) =>
      prev.map((item) =>
        item.uid === resource.uid
          ? { ...item, published: !item.published }
          : item
      )
    );
  };


  const fetchClusters = useCallback(async () => {
    if (cloudProvider === "gcp") {
      let response = await gcpClusters();
      return response;
    } else if (cloudProvider === "aws") {
      let response = await awsClusters()

      return response;
    } else {
      let response = await azureClusters()

      return response;
    }
  }, [cloudProvider]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchClusters();
        const initialPublishStates = response.reduce((acc: any, ns: any) => {
          acc[ns.id] = ns.published;
          return acc;
        }, {} as Record<string, boolean>);

        setClusterPublishStates(initialPublishStates);

        setClusters(response || []);
      } catch (error) {
        console.error("Failed to fetch clusters:", error);
        setClusters([]);
      }
    };

    fetchData();
  }, [cloudProvider]);

  const calculateAge = (creationTimestamp: string) => {
    if (!creationTimestamp) return "N/A";
    const created = new Date(creationTimestamp);
    const diffDays = Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60 * 24));
    return `${diffDays}d`;
  };

  const buildStorageParameters = (item: any) => {
    if (item.parameters && typeof item.parameters === "object") {
      return Object.entries(item.parameters)
        .map(([key, val]) => `${key}=${val}`)
        .join(", ");
    }

    // fallback defaults for common provisioners
    if (item.provisioner?.includes("azure")) return "skuName=Standard_LRS";
    if (item.provisioner?.includes("gce-pd")) return "type=pd-standard";
    if (item.provisioner?.includes("ebs.csi.aws.com")) return "type=gp3";

    return "N/A";
  };

  const extractCapacity = (item: any) => {
    if (item.capacity && item.capacity.storage) return item.capacity.storage;
    if (item.spec?.capacity?.storage) return item.spec.capacity.storage;
    return "N/A";
  };

  const extractClusterName = (clusterId: string): string => {
    if (!clusterId) return "unknown-cluster";
    const parts = clusterId.split("/");
    return parts[parts.length - 1] || "unknown-cluster";
  };

  // Helper: convert ISO → yyyy-mm-dd
  const formatDate = (iso: string): string => {
    if (!iso) return "N/A";
    const date = new Date(iso);
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchviewtab = async () => {
    if (selectedCluster) {
      let response = await viewTabDetails(selectedCluster, clusterViewTab, cloudProvider, null)

      if (clusterViewTab?.toUpperCase() === "CUSTOM_RESOURCE_DEFINITION") {
        let crdResponse = (response || []).map((item: any, index: number) => ({
          id: `crd-${index + 1}`,
          name: item.name || "-",
          apiVersion: item.apiVersion || "-",
          kind: item.kind || "CustomResourceDefinition",
          clusterId: item.clusterId || "-",
          namespace: item.namespace || "-",
          createdAt: item.creationTimestamp
            ? new Date(item.creationTimestamp).toLocaleDateString("en-GB")
            : "-",
          description: "Kubernetes Custom Resource Definition",
          published: Boolean(item.published),
          uid: item.uid
        }));


        const initialPublishStates = crdResponse.reduce((acc: any, ns: any) => {
          acc[ns.name] = ns.published;
          return acc;
        }, {} as Record<string, boolean>);

        setNamespacePublishStates(initialPublishStates);

        setCRD(crdResponse)
      } else if (clusterViewTab?.toUpperCase() === "NODE") {
        const nodesResponse = (response || []).map((item: any, index: number) => ({
          id: `node-${index + 1}`,
          name: item.name || "unknown-node",
          status: item.phase || "Unknown",
          roles: item.roles || "worker", // default if not available
          age: calculateAge(item.creationTimestamp),
          version: item.kubeletVersion || "v1.28.x", // fallback
          internalIP: item.internalIP || "N/A",
          externalIP: item.externalIP || "N/A",
          osImage: item.osImage || "Unknown OS",
          kernelVersion: item.kernelVersion || "Unknown kernel",
          containerRuntime: item.containerRuntime || "containerd://1.7.x",
          published: Boolean(item.published),
          uid: item.uid
        }));

        const initialPublishStates = nodesResponse.reduce((acc: any, ns: any) => {
          acc[ns.name] = ns.published;
          return acc;
        }, {} as Record<string, boolean>);

        setNamespacePublishStates(initialPublishStates);

        setNodes(nodesResponse);
      } else if (clusterViewTab?.toUpperCase() === "STORAGE_CLASS") {
        const storageClasses = (response || []).map((item: any, index: number) => ({
          id: `sc-${index + 1}`,
          name: item.name || "unknown-storageclass",
          provisioner: item.provisioner || "N/A",
          reclaimPolicy: item.reclaimPolicy || "Delete",
          volumeBindingMode: item.volumeBindingMode || "Immediate",
          allowVolumeExpansion: item.allowVolumeExpansion ? "true" : "false",
          parameters: buildStorageParameters(item),
          published: Boolean(item.published),
          uid: item.uid
        }));

        const initialPublishStates = storageClasses.reduce((acc: any, ns: any) => {
          acc[ns.name] = ns.published;
          return acc;
        }, {} as Record<string, boolean>);

        setNamespacePublishStates(initialPublishStates);

        setStorages(storageClasses);
      } else if (clusterViewTab?.toUpperCase() === "PERSISTENT_VOLUME") {
        const volumes = (response || []).map((item: any, index: number) => ({
          id: `pv-${index + 1}`,
          name: item.name || "unknown-pv",
          capacity: extractCapacity(item),
          accessModes: (item.accessModes && item.accessModes.join(",")) || "ReadWriteOnce",
          reclaimPolicy: item.persistentVolumeReclaimPolicy || "Delete",
          status: item.phase || "Available",
          claim: item.claimRef ? `${item.claimRef.namespace}/${item.claimRef.name}` : "Unclaimed",
          storageClass: item.storageClassName || "standard",
          reason: item.reason || "",
          age: calculateAge(item.creationTimestamp),
          published: Boolean(item.published),
          uid: item.uid
        }));

        const initialPublishStates = volumes.reduce((acc: any, ns: any) => {
          acc[ns.name] = ns.published;
          return acc;
        }, {} as Record<string, boolean>);

        setNamespacePublishStates(initialPublishStates);

        setPVT(volumes)
      } else if (clusterViewTab?.toUpperCase() === "NAMESPACE") {
        const namespacesresponse = (response || []).map((item: any, index: number) => ({
          id: `ns-${index + 1}`,
          name: item.name || "unknown",
          type: "namespace",
          cluster: extractClusterName(item.clusterId),
          createdAt: formatDate(item.creationTimestamp),
          status: (item.phase || "Unknown").toLowerCase() === "active" ? "available" : item.phase?.toLowerCase() || "unknown",
          published: Boolean(item.published),
          uid: item.uid
        }));

        const initialPublishStates = namespacesresponse.reduce((acc: any, ns: any) => {
          acc[ns.name] = ns.published;
          return acc;
        }, {} as Record<string, boolean>);

        setNamespacePublishStates(initialPublishStates);

        setNameSpaces(namespacesresponse)
      }


    }
  }

  const publishClusters = async (cluster: any) => {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;

    try {
      if (cloudProvider === "azure") {
        const selectedSubListStr = myLocalStorage.getItem("latestSubList");
        const selectedSubList = selectedSubListStr ? JSON.parse(selectedSubListStr) : null;
        const subId = selectedSubList?.azureSubscriptionId;

        const requestData = {
          resourceId: cluster?.id,
          wsTenantName: latestTenant,
          flag: !cluster.published,
          type: "AZURE_KUBERNETES",
          cloudProviderType: cloudProvider.toUpperCase(),
          resourceAccountId: subId,
        };

        await publishAzureCluster(requestData);
      } else if (cloudProvider === "aws") {
        await publishAWSCluster(cluster?.dbId ?? cluster?.id);
      } else if (cloudProvider === "gcp") {
        const latestProjectId = myLocalStorage.getItem("gcpSelectedProjectId");
        await publishGCPCluster(latestTenant, latestProjectId, cluster?.id);
      }

      setClusters(prevClusters =>
        prevClusters.map(clusterItem =>
          clusterItem.id === cluster.id
            ? { ...clusterItem, published: !clusterItem.published }
            : clusterItem
        )
      );


    } catch (error) {
      console.error("Error publishing cluster:", error);
    }
  };



  const fetchResourceTypes = async () => {
    if (selectedResourceType && selectedCluster && selectedNamespace) {
      let response = await getResourceTypes(selectedCluster, selectedResourceType, cloudProvider, selectedNamespace)
      setCurrentResources(response)

    }
  }

  const resetSelections = () => {
    setSelectedNamespace(null);
    setSelectedResourceType(null);
    setCurrentPage(1);
    setResourceSearchQuery("");
    setTypeSearchQuery("");
    setNamespaceSearchQuery("");
    setClusterPage(1)
    setSelectedPV(null)
    setSelectedStorageClass(null)
    setSelectedNode(null)
    setSelectedCRD(null)
    setClusterViewTab("namespace")
  }


  useEffect(() => {
    resetSelections();

  }, [selectedCluster])


  useEffect(() => {
    fetchviewtab();
  }, [clusterViewTab, selectedCluster])

  useEffect(() => {
    fetchResourceTypes()
  }, [selectedResourceType])

  if (selectedAnalyticsResource) {
    return (
      <CloudResourceAnalytics
        cloudProvider={selectedAnalyticsResource.cloudProvider}
        resourceId={selectedAnalyticsResource.resource.id}
        resourceName={selectedAnalyticsResource.resourceName}
        resourceType={selectedAnalyticsResource.resourceType}
        projectId={selectedAnalyticsResource.projectId}
        tenantName={selectedAnalyticsResource.tenantName}
        resource={selectedAnalyticsResource.resource}
        onBack={() => {
          setSelectedAnalyticsResource(null);
          onAnalyticsViewChange?.(false);
        }}
        flushHorizontalPadding
      />
    );
  }

  // Cluster List View
  if (!selectedCluster) {
    return (
      <div className="k8s-page">
          <Card className="cloud-card-surface rounded-2xl">
            <CardContent className="p-3 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search clusters by name, region, or group..."
                value={clusterSearchQuery}
                onChange={(e) => handleClusterSearch(e.target.value)}
                className="pl-10 h-11 rounded-xl bg-card border-border/50 shadow-sm focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-border/50 focus:border-border/50"
                data-testid="input-search-clusters"
              />
            </div>
            </CardContent>
          </Card>

          {/* Clusters Grid */}
          {paginatedClusters.length === 0 ? (
            <Card className="cloud-card-surface border-dashed rounded-2xl">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                  <SiKubernetes className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No {cloudProvider.toUpperCase()} clusters found</h3>
                <p className="text-sm text-muted-foreground max-w-md mb-6">
                  You currently don't have any clusters for this account.
                </p>
                {/**
                                                                                           * <Button variant="outline" onClick={() => navigate("/aws/users")} data-testid="button-go-to-resources">
                                                                                            Go to Resources
                                                                                          </Button>
                                                                                           */}

              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {paginatedClusters.map((cluster) => {

                  return (
                    <Card
                      key={cluster.id}
                      className="cloud-list-card cursor-pointer transition-all rounded-2xl flex flex-col"
                      onClick={() => openResourceAnalytics(cluster, "kubernetes_cluster")}
                      data-testid={`card-cluster-${cluster.id}`}
                    >
                      <CardHeader className="pb-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <SiKubernetes className="w-6 h-6 text-primary" />
                          </div>
                          {cluster.type !== '-' && (
                            <Badge variant="secondary" className="text-xs">
                              {cluster.type}
                            </Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="flex flex-col flex-1">
                        <div className="flex-1 space-y-4">
                          <div>
                            <h3 className="font-medium text-sm mb-1 break-all">
                              {cluster.name}
                            </h3>
                            {cluster.groupName !== '-' && (
                              <p className="text-xs text-muted-foreground line-clamp-2">
                                {cluster.groupName} Environment
                              </p>
                            )}
                          </div>

                          <div className="space-y-2 pt-2 border-t text-xs">
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-muted-foreground">Region:</span>
                              <span className="font-medium">{cluster.region}</span>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-muted-foreground whitespace-nowrap">Activity:</span>
                              <div className="flex items-center gap-2 flex-1 justify-end">
                                <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-primary rounded-full transition-all duration-300"
                                    style={{ width: `${Math.min(Math.max(Number(cluster.activity) || 0, 0), 100)}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-muted-foreground">Published:</span>
                              <Switch
                                checked={cluster.published}
                                onCheckedChange={(checked) => {
                                  publishClusters(cluster);

                                }}
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`switch-publish-cluster-${cluster.id}`}
                              />
                            </div>
                          </div>
                        </div>

                        <Button
                          className="w-full gap-2 text-primary hover:text-primary mt-4"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCluster(cluster.id);
                          }}
                          data-testid={`button-cluster-${cluster.id}`}
                        >
                          View Resources
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Cluster Pagination */}
              {totalClusterPages > 1 && (
                <div className="cloud-card-surface rounded-2xl flex items-center justify-between p-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {clusterStartIndex + 1}-{Math.min(clusterStartIndex + ITEMS_PER_PAGE, filteredClusters.length)} of {filteredClusters.length}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setClusterPage(p => Math.max(1, p - 1))}
                      disabled={clusterPage === 1}
                      data-testid="button-cluster-prev-page"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>
                    <div className="text-sm">
                      Page {clusterPage} of {totalClusterPages}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setClusterPage(p => Math.min(totalClusterPages, p + 1))}
                      disabled={clusterPage === totalClusterPages}
                      data-testid="button-cluster-next-page"
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
      </div>
    );
  }

  // Cluster Detail View - Show namespaces, resource types, and resources
  const currentCluster = clusters.find(c => c.id === selectedCluster);
  return (
    <div className="k8s-page">
      <Card className="cloud-card-surface rounded-2xl">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackToList}
              className="h-10 w-10 shrink-0 rounded-[10px] border border-border/50 bg-background/70 text-muted-foreground shadow-sm hover:bg-background/90 hover:text-foreground"
              data-testid="button-back-to-clusters"
              aria-label="Back to clusters"
              title="Back to clusters"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex min-w-0 items-center gap-2">
              <SiKubernetes className="w-6 h-6 shrink-0 text-primary" />
              <h2 className="truncate font-semibold text-lg">{currentCluster?.name}</h2>
              <span className="whitespace-nowrap text-sm text-muted-foreground">({currentCluster?.region})</span>
            </div>
            <div className="cs-tabs !m-0 !ml-auto !w-auto !bg-transparent flex-wrap justify-end">
              <button
                onClick={() => setClusterViewTab("namespace")}
                className={`cs-tab ${clusterViewTab === "namespace" ? "active" : ""}`}
                data-testid="tab-namespace"
              >
                Namespace
              </button>
              <button
                onClick={() => setClusterViewTab("CUSTOM_RESOURCE_DEFINITION")}
                className={`cs-tab ${clusterViewTab === "CUSTOM_RESOURCE_DEFINITION" ? "active" : ""}`}
                data-testid="tab-crd"
              >
                Custom Resource Definition
              </button>
              <button
                onClick={() => setClusterViewTab("node")}
                className={`cs-tab ${clusterViewTab === "node" ? "active" : ""}`}
                data-testid="tab-node"
              >
                Node
              </button>
              <button
                onClick={() => setClusterViewTab("storage_class")}
                className={`cs-tab ${clusterViewTab === "storage_class" ? "active" : ""}`}
                data-testid="tab-storage-class"
              >
                Storage Class
              </button>
              <button
                onClick={() => setClusterViewTab("persistent_volume")}
                className={`cs-tab ${clusterViewTab === "persistent_volume" ? "active" : ""}`}
                data-testid="tab-persistent-volume"
              >
                Persistent Volume
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Namespace Tab Content (existing flow) */}
      {clusterViewTab === "namespace" ? (
        <div className="k8s-detail-shell">
          {/* Namespaces Sidebar */}
          <div className="w-64 k8s-panel flex flex-col flex-shrink-0">
            <div className="p-3 k8s-panel-header">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search namespaces..."
                  value={namespaceSearchQuery}
                  onChange={(e) => setNamespaceSearchQuery(e.target.value)}
                  className="k8s-search-input pl-10"
                  data-testid="input-search-namespaces"
                />
              </div>
            </div>
            <div className="flex-1 overflow-auto k8s-panel-list">
              <div className="space-y-1">
                {filteredNamespaces.map((namespace) => {
                  const isSelected = selectedNamespace === namespace.name;
                  console.log(namespace)

                  return (
                    <div
                      key={namespace.id}
                      className="k8s-panel-item overflow-hidden"
                    >
                      <button
                        onClick={() => {
                          setSelectedNamespace(namespace.name);
                          setSelectedResourceType(null);
                          setCurrentPage(1);
                          setResourceSearchQuery("");
                        }}
                        className={`w-full transition-colors ${isSelected
                          ? "bg-primary text-primary-foreground"
                          : "hover:bg-background/50"
                          }`}
                        data-testid={`button-namespace-${namespace.name}`}
                      >
                        <div className="px-3 py-3">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2.5 flex-1 min-w-0">
                              <Layers className="w-4 h-4 flex-shrink-0" />
                              <span className="font-medium truncate text-sm">{namespace.name}</span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs opacity-70">Publish</span>
                            <Switch
                              disabled={!currentCluster?.published}
                              checked={namespace.published}
                              onCheckedChange={(checked) => {
                                toggleResourceCategoryPublish(namespace, currentCluster, clusterViewTab);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`switch-publish-${namespace.name}`}
                            />
                          </div>
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Resource Types and Resources Content */}
          {!selectedNamespace ? (
            <div className="flex-1 k8s-panel flex items-center justify-center">
              <div className="text-center">
                <Layers className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">Select a namespace to view resources</p>
              </div>
            </div>
          ) : (
            <>
              {/* Resource Types Sidebar */}
              <div className="w-64 k8s-panel flex flex-col flex-shrink-0">
                <div className="p-3 k8s-panel-header">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search types..."
                      value={typeSearchQuery}
                      onChange={(e) => setTypeSearchQuery(e.target.value)}
                      className="k8s-search-input pl-10"
                      data-testid="input-search-types"
                    />
                  </div>
                </div>
                <div className="flex-1 overflow-auto k8s-panel-list">
                  <div className="space-y-1">
                    {nameSpaceLevelType.map((type) => {
                      const isSelected = selectedResourceType === String(type.type);
                      const Icon = type.icon;
                      return (
                        <button
                          key={type.id}
                          onClick={() => handleResourceTypeChange(String(type.type))}
                          className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${isSelected
                            ? "bg-primary text-primary-foreground"
                            : "hover:bg-background/50"
                            }`}
                          data-testid={`button-type-${type.id}`}
                        >
                          <Icon className="w-4 h-4 flex-shrink-0" />
                          <div className="flex items-center gap-2.5 flex-1 min-w-0">
                            <span className="font-medium truncate">{type.name}</span>
                          </div>

                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Resources Content */}
              <div className="flex-1 k8s-panel flex flex-col">
                <div className="p-4 k8s-panel-header">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">
                        {resourceTypes.find(t => t.id === selectedResourceType)?.name || "Resources"}
                      </h3>
                      <span className="text-sm text-muted-foreground">
                        ({searchFilteredResources.length} {searchFilteredResources.length === 1 ? 'item' : 'items'})
                      </span>
                    </div>
                  </div>
                  <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search resources..."
                      value={resourceSearchQuery}
                      onChange={(e) => handleResourceSearch(e.target.value)}
                      className="k8s-search-input pl-10"
                      data-testid="input-search-resources"
                    />
                  </div>
                </div>
                <ScrollArea className="flex-1">
                  <div className="p-4">
                    {paginatedResources.length === 0 ? (
                      <div className="text-center py-12">
                          <Card className="cloud-card-surface border-dashed rounded-2xl">
                            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                                <SiKubernetes className="w-8 h-8 text-muted-foreground" />
                              </div>
                              <h3 className="text-lg font-semibold mb-2">No Resource Available</h3>
                              <p className="text-sm text-muted-foreground max-w-md mb-6">
                                You currently don't have any resources in this cluster.
                              </p>

                            </CardContent>
                          </Card>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                          {paginatedResources.map((resource) => {
                            const typeInfo = resourceTypes.find(t => t.id === resource.type) || resourceTypes[0];
                            const Icon = typeInfo.icon;

                            return (
                              <Card
                                key={resource.id}
                                className="cloud-list-card cursor-pointer rounded-2xl"
                                onClick={() => openResourceAnalytics(resource, selectedResourceType || resource.type || "kubernetes_resource")}
                                data-testid={`card-resource-${resource.id}`}
                              >
                                <CardHeader className="pb-3">
                                  <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                        <Icon className="w-6 h-6 text-primary" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <h4 className="font-medium text-sm text-foreground mb-1 break-words">{resource.name}</h4>
                                        <p className="text-xs text-muted-foreground">
                                          {resource.namespace || 'Cluster-wide'}
                                        </p>
                                      </div>
                                    </div>
                                    <div onClick={(event) => event.stopPropagation()}>
                                      <DetailsDialog
                                        data={resource}
                                        title={`${resource.name} Details`}
                                        trigger="icon"
                                        testId={`button-info-${resource.id}`}
                                      />
                                    </div>
                                  </div>
                                </CardHeader>
                                <CardContent className="space-y-3 text-xs">
                                  <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">Status</span>
                                    <Badge variant={String(resource.status).toLowerCase() === "running" ? "default" : "secondary"} className="text-[10px] leading-4">
                                      {resource.status || "Synced"}
                                    </Badge>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">Created</span>
                                    <span className="font-medium">{resource.createdAt}</span>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">Region</span>
                                    <span className="font-medium">{resource.region || currentCluster?.region || "-"}</span>
                                  </div>
                                  <div className="flex items-center justify-between gap-3">
                                    <span className="text-muted-foreground whitespace-nowrap">Activity</span>
                                    <div className="flex flex-1 items-center justify-end gap-2">
                                      <div className="h-2 w-24 overflow-hidden rounded-full bg-muted">
                                        <div
                                          className="h-full rounded-full bg-primary transition-all duration-300"
                                          style={{ width: `${Math.min(Math.max(Number(resource.activity) || 0, 0), 100)}%` }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center justify-between pt-2 border-t">
                                    <span className="text-sm font-medium text-muted-foreground">Publish</span>
                                    <Switch
                                      checked={resource.published}
                                      onCheckedChange={() => toggleResourcePublish(resource, currentCluster, selectedResourceType || "")}
                                      onClick={(event) => event.stopPropagation()}
                                      data-testid={`switch-resource-publish-${resource.id}`}
                                    />
                                  </div>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                          <div className="cloud-card-surface rounded-2xl flex items-center justify-between mt-6 p-4">
                            <div className="text-sm text-muted-foreground">
                              Showing {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, searchFilteredResources.length)} of {searchFilteredResources.length}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                data-testid="button-prev-page"
                              >
                                <ChevronLeft className="w-4 h-4" />
                                Previous
                              </Button>
                              <div className="text-sm">
                                Page {currentPage} of {totalPages}
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                data-testid="button-next-page"
                              >
                                Next
                                <ChevronRight className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </ScrollArea>
              </div>
            </>
          )}
        </div>
      ) : clusterViewTab === "CUSTOM_RESOURCE_DEFINITION" ? (
        <div className="k8s-detail-shell">
          <div className="k8s-panel k8s-split-list flex flex-col">
            <div className="p-4 k8s-panel-header">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search Custom Resource Definitions..."
                  className="k8s-search-input pl-10"
                  value={crdSearchQuery}
                  onChange={(e) => setCrdSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <ScrollArea className="flex-1">
              <div className="k8s-panel-list space-y-2">
                {filteredCRDs.map((crd) => (
                  <div
                    key={crd.id}
                    onClick={() => setSelectedCRD(crd)}
                    className={`cloud-list-card px-4 py-3.5 rounded-2xl cursor-pointer transition-all border ${selectedCRD?.id === crd.id
                      ? "bg-primary/5 border-primary"
                      : "bg-card border-border"
                      }`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        <FileCode className="w-4 h-4 text-primary flex-shrink-0" />
                        <span className="font-medium text-sm truncate">{crd.name}</span>
                      </div>
                      <div className="flex items-center gap-2.5 flex-shrink-0">
                        <span className="text-xs text-muted-foreground">Publish</span>
                        <Switch checked={crd.published} onClick={(e) => toggleResourceCategoryPublish(crd, currentCluster, clusterViewTab)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
          {/* Properties Panel - Right Side */}
          <div className="k8s-panel k8s-split-details flex flex-col">
            <div className="p-4 k8s-panel-header">
              <h2 className="text-lg font-semibold">{selectedCRD?.name || "Select a Custom Resource Definition"}</h2>
            </div>
            <ScrollArea className="flex-1">
              {selectedCRD && (
                <div className="p-4 space-y-4">
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Name</div>
                    <div className="text-sm font-normal text-foreground">{selectedCRD.name}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">API Version</div>
                    <div className="text-sm font-normal text-foreground">{selectedCRD.apiVersion}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">UID</div>
                    <div className="text-sm font-normal text-foreground">{selectedCRD.uid}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Kind</div>
                    <div className="text-sm font-normal text-foreground">{selectedCRD.kind}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Phase</div>
                    <div className="text-sm font-normal text-foreground">{selectedCRD.phase}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Creation Timestamp</div>
                    <div className="text-sm font-normal text-foreground">{selectedCRD.creationTimestamp}</div>
                  </div>
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      ) : clusterViewTab === "node" ? (
        <div className="k8s-detail-shell">
          <div className="k8s-panel k8s-split-list flex flex-col">
            <div className="p-4 k8s-panel-header">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search Nodes..."
                  className="k8s-search-input pl-10"
                  value={nodeSearchQuery}
                  onChange={(e) => setNodeSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <ScrollArea className="flex-1">
              <div className="k8s-panel-list space-y-2">
                {filteredNodes.map((node) => (
                  <div
                    key={node.id}
                    onClick={() => setSelectedNode(node)}
                    className={`cloud-list-card px-4 py-3.5 rounded-2xl cursor-pointer transition-all border ${selectedNode?.id === node.id
                      ? "bg-primary/5 border-primary"
                      : "bg-card border-border"
                      }`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        <Server className="w-4 h-4 text-primary flex-shrink-0" />
                        <span className="font-medium text-sm truncate">{node.name}</span>
                      </div>
                      <div className="flex items-center gap-2.5 flex-shrink-0">
                        <span className="text-xs text-muted-foreground">Publish</span>
                        <Switch checked={node.published} onClick={(e) => toggleResourceCategoryPublish(node, currentCluster, clusterViewTab)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
          <div className="k8s-panel k8s-split-details flex flex-col">
            <div className="p-4 k8s-panel-header">
              <h2 className="text-lg font-semibold">{selectedNode?.name || "Select a Node"}</h2>
            </div>
            <ScrollArea className="flex-1">
              {selectedNode && (
                <div className="p-4 space-y-4">
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Name</div>
                    <div className="text-sm font-normal text-foreground">{selectedNode.name}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Status</div>
                    <div className="text-sm font-normal text-foreground">{selectedNode.status}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Roles</div>
                    <div className="text-sm font-normal text-foreground">{selectedNode.roles}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Version</div>
                    <div className="text-sm font-normal text-foreground">{selectedNode.version}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Internal IP</div>
                    <div className="text-sm font-normal text-foreground">{selectedNode.internalIP}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">External IP</div>
                    <div className="text-sm font-normal text-foreground">{selectedNode.externalIP}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">OS Image</div>
                    <div className="text-sm font-normal text-foreground">{selectedNode.osImage}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Container Runtime</div>
                    <div className="text-sm font-normal text-foreground">{selectedNode.containerRuntime}</div>
                  </div>
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      ) : clusterViewTab === "storage_class" ? (
        <div className="k8s-detail-shell">
          <div className="k8s-panel k8s-split-list flex flex-col">
            <div className="p-4 k8s-panel-header">
              <div className="relative">

                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search Storage Classes..."
                  className="k8s-search-input pl-10"
                  value={storageClassSearchQuery}
                  onChange={(e) => setStorageClassSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <ScrollArea className="flex-1">
              <div className="k8s-panel-list space-y-2">
                {filteredStorageClasses.map((sc) => (
                  <div
                    key={sc.id}
                    onClick={() => setSelectedStorageClass(sc)}
                    className={`cloud-list-card px-4 py-3.5 rounded-2xl cursor-pointer transition-all border ${selectedStorageClass?.id === sc.id
                      ? "bg-primary/5 border-primary"
                      : "bg-card border-border"
                      }`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        <Database className="w-4 h-4 text-primary flex-shrink-0" />
                        <span className="font-medium text-sm truncate">{sc.name}</span>
                      </div>
                      <div className="flex items-center gap-2.5 flex-shrink-0">
                        <span className="text-xs text-muted-foreground">Publish</span>
                        <Switch checked={sc.published} onClick={(e) => toggleResourceCategoryPublish(sc, currentCluster, clusterViewTab)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
          <div className="k8s-panel k8s-split-details flex flex-col">
            <div className="p-4 k8s-panel-header">
              <h2 className="text-lg font-semibold">{selectedStorageClass?.name || "Select a Storage Class"}</h2>
            </div>
            <ScrollArea className="flex-1">
              {selectedStorageClass && (
                <div className="p-4 space-y-4">
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Name</div>
                    <div className="text-sm font-normal text-foreground">{selectedStorageClass.name}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Provisioner</div>
                    <div className="text-sm font-normal text-foreground">{selectedStorageClass.provisioner}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Reclaim Policy</div>
                    <div className="text-sm font-normal text-foreground">{selectedStorageClass.reclaimPolicy}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Volume Binding Mode</div>
                    <div className="text-sm font-normal text-foreground">{selectedStorageClass.volumeBindingMode}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Allow Volume Expansion</div>
                    <div className="text-sm font-normal text-foreground">{selectedStorageClass.allowVolumeExpansion}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Parameters</div>
                    <div className="text-sm font-normal text-foreground">{selectedStorageClass.parameters}</div>
                  </div>
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      ) : clusterViewTab === "persistent_volume" ? (
        <div className="k8s-detail-shell">
          <div className="k8s-panel k8s-split-list flex flex-col">
            <div className="p-4 k8s-panel-header">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search Persistent Volumes..."
                  className="k8s-search-input pl-10"
                  value={pvSearchQuery}
                  onChange={(e) => setPvSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <ScrollArea className="flex-1">
              <div className="k8s-panel-list space-y-2">
                {filteredPVs.map((pv) => (
                  <div
                    key={pv.id}
                    onClick={() => setSelectedPV(pv)}
                    className={`cloud-list-card px-4 py-3.5 rounded-2xl cursor-pointer transition-all border ${selectedPV?.id === pv.id
                      ? "bg-primary/5 border-primary"
                      : "bg-card border-border"
                      }`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        <HardDrive className="w-4 h-4 text-primary flex-shrink-0" />
                        <span className="font-medium text-sm truncate">{pv.name}</span>
                      </div>
                      <div className="flex items-center gap-2.5 flex-shrink-0">
                        <span className="text-xs text-muted-foreground">Publish</span>
                        <Switch checked={pv.published} onClick={(e) => toggleResourceCategoryPublish(pv, currentCluster, clusterViewTab)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
          <div className="k8s-panel k8s-split-details flex flex-col">
            <div className="p-4 k8s-panel-header">
              <h2 className="text-lg font-semibold">{selectedPV?.name || "Select a Persistent Volume"}</h2>
            </div>
            <ScrollArea className="flex-1">
              {selectedPV && (
                <div className="p-4 space-y-4">
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Name</div>
                    <div className="text-sm font-normal text-foreground">{selectedPV.name}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Capacity</div>
                    <div className="text-sm font-normal text-foreground">{selectedPV.capacity}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Access Modes</div>
                    <div className="text-sm font-normal text-foreground">{selectedPV.accessModes}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Reclaim Policy</div>
                    <div className="text-sm font-normal text-foreground">{selectedPV.reclaimPolicy}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Status</div>
                    <div className="text-sm font-normal text-foreground">{selectedPV.status}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Claim</div>
                    <div className="text-sm font-normal text-foreground">{selectedPV.claim}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Storage Class</div>
                    <div className="text-sm font-normal text-foreground">{selectedPV.storageClass}</div>
                  </div>
                  <div className="cloud-card-surface p-4 rounded-2xl space-y-1">
                    <div className="text-sm font-medium text-muted-foreground">Age</div>
                    <div className="text-sm font-normal text-foreground">{selectedPV.age}</div>
                  </div>
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      ) : null}

    </div>
  );
}
